/** Spread the scenes over the board, one frame each, so the camera can travel between them. */

import { X } from "lucide-react";
import { useState } from "react";
import { defaultColumns, frameGroups, spreadScenes } from "./actions";
import { useEditor } from "./store";

export function SpreadDialog() {
  const project = useEditor((s) => s.project)!;
  const groups = frameGroups(project);
  const n = groups.length;
  const [columns, setColumns] = useState(defaultColumns(n));
  const [gap, setGap] = useState(0.25);
  const [pan, setPan] = useState(true);
  const close = () => useEditor.getState().set({ panel: null });
  const cols = Math.max(1, Math.min(columns, n));
  const rows = Math.ceil(n / cols);
  const pans = project.scenes.filter((s) => s.transition.type === "pan").length;

  // the map: frames in snake order with the camera's path through them
  const cell = Math.min(34, 330 / cols);
  const w = cell * (1 + gap);
  const h = cell * (9 / 16) * (1 + gap);
  const at = (k: number) => {
    const row = Math.floor(k / cols);
    const col = row % 2 === 0 ? k % cols : cols - 1 - (k % cols);
    return { x: col * w, y: row * h };
  };
  const path = groups.map((_, k) => at(k)).map((p) => `${p.x + cell / 2},${p.y + (cell * 9) / 32}`).join(" ");

  const apply = () => {
    spreadScenes({ columns: cols, gap, pan });
    useEditor.getState().set({ panel: null, boardView: true });
  };

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal spread" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Spread scenes across the board</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <p className="muted small">
          Each scene gets its own place on the whiteboard and its layers move with it. Scenes set to “Build on the previous scene” share its
          place. {n} place{n === 1 ? "" : "s"} for {project.scenes.length} scenes, in reading order, snaking back on every other row so the camera only
          moves to a neighbour. Undo (Ctrl+Z) puts everything back.
        </p>
        <div className="spread-body">
          <div className="spread-form">
            <label>Columns <input type="number" min={1} max={Math.max(1, n)} value={columns} onChange={(e) => setColumns(Math.max(1, Number(e.target.value) || 1))} /></label>
            <label>Space between
              <select value={gap} onChange={(e) => setGap(Number(e.target.value))}>
                <option value={0.1}>small</option>
                <option value={0.25}>medium</option>
                <option value={0.5}>large</option>
              </select>
            </label>
            <label className="check" title="The camera glides from one scene to the next and earlier drawings stay on the board. Without it, each scene keeps its transition (erase, wipe, cut).">
              <input type="checkbox" checked={pan} onChange={(e) => setPan(e.target.checked)} /> Pan the camera to each new place
            </label>
            <p className="muted small">
              {cols} × {rows} board{pan ? `; ${n - 1} transitions become pans` : ""}{!pan && pans ? `; ${pans} pan transition${pans === 1 ? "" : "s"} kept` : ""}.
              You can change any scene's transition afterwards in the scene inspector.
            </p>
          </div>
          <svg className="spread-map" width={cols * w} height={rows * h} viewBox={`-2 -2 ${cols * w + 4} ${rows * h + 4}`}>
            {groups.map((g, k) => {
              const p = at(k);
              return (
                <g key={g[0].id}>
                  <rect x={p.x} y={p.y} width={cell} height={(cell * 9) / 16} rx={2} />
                  {cell >= 22 && <text x={p.x + cell / 2} y={p.y + (cell * 9) / 32 + 3}>{k + 1}</text>}
                </g>
              );
            })}
            {pan && n > 1 && <polyline points={path} />}
          </svg>
        </div>
        <div className="analysis-actions">
          <button onClick={close}>Cancel</button>
          <button className="primary" onClick={apply}>Spread {n} place{n === 1 ? "" : "s"}</button>
        </div>
      </div>
    </div>
  );
}
